






















































































































/* General */
import Vue from 'vue'
import { uniq } from 'lodash'

/* Mixins */

/* Components */
import SpamHeader from '@/components/conversation/spam-header.vue'

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import RouteAssistant from '@/helpers/route-assistant'
import TypeAssistant from '@/helpers/type-assistant'
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { ILabelValuePair } from '@/types/data-objects/global'
import { IBreadcrumbItemMenuItem } from '@/types/data-objects/items'
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IConvosOutputDetail,
  IConvosOutputHistoryv12,
  IItemReference,
  ILinkedItemForListing,
} from '@/api/types/response.types'
import { EEntities } from '@/types'

/* API */
import ConversationsAPI from '@/api/modules/convos.api'
import MailboxesAPI from '@/api/modules/mailboxes.api'

export default Vue.extend({
  name: 'ConversationDetailDrawer',

  components: {
    SpamHeader,
  },

  props: {
    showDrawer: {
      type: Boolean,
      default: false,
    },
    /* If hydrated, allows unlinking from task */
    originatingTask: {
      type: Object as () => IItemReference,
      default: null,
    },
    conversation: {
      type: Object as () => ILinkedItemForListing,
      required: true,
    },
    prevItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
    nextItem: {
      type: Object as () => ILinkedItemForListing,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      ColorAssistant,
      drawerOpened: false,
      isDataLoaded: false,
      isAccessible: false,
      isRequestingAccess: false,
      mailboxesRequestedAccessTo: [] as ILabelValuePair[],
      details: {} as IConvosOutputDetail,
      globalAttachments: [] as IAttachment[],
      history: [] as IConvosOutputHistoryv12[],
    }
  },

  computed: {
    visible: {
      get(): Boolean {
        return this.showDrawer
      },
      set(newValue: boolean) {
        this.$emit('update:showDrawer', newValue)
      },
    },
    drawerWidthPct(): string {
      if (this.$screen.isMobile(this.$mq)) {
        return '100%'
      }
      return '524px'
    },
    prevItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.prevItem) {
        return null
      }
      return {
        itemID: this.prevItem.id,
        entityID: this.prevItem.entity.id,
        entityName: this.prevItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.MAILBOXES),
        refNumber: this.prevItem.refNumber,
        title: this.prevItem.title,
      }
    },
    nextItemForNav(): IBreadcrumbItemMenuItem {
      if (!this.nextItem) {
        return null
      }
      return {
        itemID: this.nextItem.id,
        entityID: this.nextItem.entity.id,
        entityName: this.nextItem.entity.name,
        entityType: TextAssistant.entitySingularName(EEntities.MAILBOXES),
        refNumber: this.nextItem.refNumber,
        title: this.nextItem.title,
      }
    },
    requestedAccessToMailbox(): boolean {
      return this.mailboxesRequestedAccessTo.some(p => {
        return p.value === this.conversation.entity.id
      })
    },
    mailboxRequestMessage(): string {
      return this.mailboxesRequestedAccessTo.find(p => {
        return p.value === this.conversation.entity.id
      }).label
    },
    unlinkLabel(): string {
      return 'Unlink from #' + this.originatingTask.refNumber
    },
    spamType(): number {
      return TypeAssistant.getSpamType(this.details.spamStatus)
    },
    isSpam(): boolean {
      return TypeAssistant.isSpam(this.spamType)
    },
  },

  watch: {
    conversation: {
      /* Subsequent data fetches (prev/next arrows). Check for drawerOpened to avoid
         double fetching on initial conversation load */
      async handler(value) {
        if (this.drawerOpened) {
          await this.fetchData()
        }
      },
    },
    drawerOpened: {
      /* Initial data fetch should happen only after drawer finishes opening */
      async handler(value) {
        if (value) {
          await this.fetchData()
        }
      },
    },
  },

  methods: {
    async onOpened() {
      this.drawerOpened = true
    },

    onClosed() {
      this.drawerOpened = false
      this.isDataLoaded = false
      this.clearData()
    },

    clearData() {
      this.details = {} as IConvosOutputDetail
      this.globalAttachments = [] as IAttachment[]
      this.history = [] as IConvosOutputHistoryv12[]
    },

    async fetchData() {
      this.isDataLoaded = false
      try {
        await Promise.all([this.fetchHistory(), this.fetchConversationData()])
        this.isAccessible = true
      } catch (error) {
        if (error.status === 403) {
          this.isAccessible = false
        } else {
          this.$error.emit(error, true)
        }
      } finally {
        this.isDataLoaded = true
      }
    },

    async fetchHistory() {
      const response = await ConversationsAPI.get_History_v1_2(
        this.conversation.entity.id,
        this.conversation.id,
        'chron',
      )

      const allAttachments: IAttachment[] = []
      response.forEach(item => {
        allAttachments.push(...item.attachments)
      })
      this.globalAttachments = uniq(allAttachments)
      this.history = response
    },

    async fetchConversationData() {
      this.details = await ConversationsAPI.get_Detail(
        this.conversation.entity.id,
        this.conversation.id,
      )
    },

    async requestAccess() {
      try {
        this.isRequestingAccess = true
        const response = await MailboxesAPI.put_RequestAccess(
          this.conversation.entity.id,
        )
        const mailboxAccessRequested = {
          label: response.message,
          value: this.conversation.entity.id,
        }
        this.mailboxesRequestedAccessTo.push(mailboxAccessRequested)
      } catch (error) {
        this.$error.emit(error, true)
      } finally {
        this.isRequestingAccess = false
      }
    },

    goToConversation(newWindow?: boolean) {
      RouteAssistant.goToConversation(
        this.$router,
        this.$route.params.accountId,
        this.conversation.entity.id,
        this.conversation.id,
        this.conversation.entity.name,
        this.conversation.refNumber,
        this.conversation.title,
        newWindow,
      )
    },
  },
})
